import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Sheet, List, ListItem, ListItemButton, IconButton, Typography } from '@mui/joy';
import {
  HomeIcon,
  ShieldCheckIcon,
  DocumentMagnifyingGlassIcon,
  BellIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@utils/cn';
import { useAuth } from '@contexts/AuthContext';

const menuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: HomeIcon },
  { path: '/security-audit', label: 'Security Audit', icon: ShieldCheckIcon },
  { path: '/vulnerability-scan', label: 'Vulnerability Scan', icon: DocumentMagnifyingGlassIcon },
  { path: '/notifications', label: 'Notifications', icon: BellIcon },
  { path: '/settings', label: 'Settings', icon: Cog6ToothIcon },
];

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sheet
        className={cn(
          'fixed left-0 top-0 h-full z-50 transition-all duration-300',
          'bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700',
          sidebarOpen ? 'w-64' : 'w-20',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <ShieldCheckIcon className="w-8 h-8 text-primary-600" />
              {sidebarOpen && (
                <Typography level="h4" className="font-bold text-primary-600">
                  FortifyTech
                </Typography>
              )}
            </div>
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex"
            >
              {sidebarOpen ? <XMarkIcon className="w-5 h-5" /> : <Bars3Icon className="w-5 h-5" />}
            </IconButton>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4">
            <List>
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <ListItem key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        cn(
                          'w-full rounded-lg transition-colors',
                          isActive
                            ? 'bg-primary-100 dark:bg-primary-900 text-primary-600'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        )
                      }
                    >
                      <ListItemButton className="flex items-center gap-3 px-3 py-2">
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        {sidebarOpen && (
                          <span className="text-sm font-medium">{item.label}</span>
                        )}
                      </ListItemButton>
                    </NavLink>
                  </ListItem>
                );
              })}
            </List>
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-700">
            <div className={cn('flex items-center', sidebarOpen ? 'gap-3' : 'justify-center')}>
              <UserCircleIcon className="w-8 h-8 text-gray-600 dark:text-gray-400" />
              {sidebarOpen && (
                <div className="flex-1">
                  <Typography level="body-sm" className="font-medium truncate">
                    {user?.name || user?.email?.split('@')[0] || 'User'}
                  </Typography>
                  <Typography level="body-xs" className="text-gray-500 truncate">
                    {user?.email}
                  </Typography>
                </div>
              )}
              {sidebarOpen && (
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => logout()}
                  title="Sign out"
                >
                  <ArrowRightOnRectangleIcon className="w-5 h-5" />
                </IconButton>
              )}
            </div>
          </div>
        </div>
      </Sheet>

      {/* Main content */}
      <div
        className={cn(
          'transition-all duration-300',
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
        )}
      >
        {/* Top bar */}
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-3">
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden"
            >
              <Bars3Icon className="w-6 h-6" />
            </IconButton>

            <div className="flex items-center gap-4 ml-auto">
              <IconButton size="sm" variant="plain">
                <BellIcon className="w-5 h-5" />
              </IconButton>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};